import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Post } from '../utils/firebase-helpers';

export type PostFilter = 'recent' | 'popular' | 'oldest' | 'following' | 'trending';

interface UseFilteredPostsProps {
  userId?: string;
  filter?: PostFilter;
  limitCount?: number;
}

/**
 * Hook para obtener publicaciones filtradas según diferentes criterios
 * @param userId ID del usuario actual (opcional)
 * @param filter Filtro a aplicar: 'recent', 'popular', 'oldest', 'following', 'trending'
 * @param limitCount Número máximo de publicaciones a obtener
 */
export function useFilteredPosts({ userId, filter = 'recent', limitCount = 20 }: UseFilteredPostsProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Función para obtener las publicaciones de las personas que sigue el usuario
    const fetchFollowingPosts = async () => {
      if (!userId) {
        setPosts([]);
        setLoading(false);
        return undefined;
      }

      try {
        // Obtener IDs de usuarios seguidos
        const followingRef = collection(db, `users/${userId}/following`);
        const followingSnapshot = await getDocs(followingRef);

        if (followingSnapshot.empty) {
          setPosts([]);
          setLoading(false);
          return undefined;
        }

        const followingIds = followingSnapshot.docs.map(doc => doc.id);

        // Incluir también los posts del propio usuario
        followingIds.push(userId);

        // Consultar posts de usuarios seguidos
        const postsRef = collection(db, 'posts');
        const postsQuery = query(
          postsRef,
          where('userId', 'in', followingIds),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );

        const unsubscribe = onSnapshot(
          postsQuery,
          (snapshot) => {
            const fetchedPosts: Post[] = [];
            snapshot.forEach((doc) => {
              fetchedPosts.push({ id: doc.id, ...doc.data() } as Post);
            });
            setPosts(fetchedPosts);
            setLoading(false);
          },
          (err) => {
            console.error('Error fetching following posts:', err);
            setError('Error al cargar las publicaciones de usuarios seguidos');
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (error) {
        console.error('Error en fetchFollowingPosts:', error);
        setError('Error al cargar las publicaciones');
        setLoading(false);
        return undefined;
      }
    };

    // Función para obtener publicaciones con tendencia (más comentarios e interacciones)
    const fetchTrendingPosts = async () => {
      try {
        const postsRef = collection(db, 'posts');

        // Calcular la fecha de hace 7 días
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        // Primero obtenemos los posts recientes
        // Nota: Firestore tiene limitaciones con consultas compuestas, así que simplificamos
        // la consulta inicial y luego ordenamos en memoria
        const postsQuery = query(
          postsRef,
          where('createdAt', '>=', Timestamp.fromDate(oneWeekAgo)),
          orderBy('createdAt', 'desc'),
          limit(limitCount * 2) // Obtenemos más posts para luego filtrar los mejores
        );

        const unsubscribe = onSnapshot(
          postsQuery,
          (snapshot) => {
            const fetchedPosts: Post[] = [];
            snapshot.forEach((doc) => {
              fetchedPosts.push({ id: doc.id, ...doc.data() } as Post);
            });

            // Ordenar por una combinación de recencia, likes y comentarios
            const sortedPosts = fetchedPosts.sort((a, b) => {
              // Calcular una puntuación basada en likes, comentarios y recencia
              // Damos más peso a los likes y comentarios para las tendencias
              const likesA = a.likes || 0;
              const commentsA = a.commentsCount || 0;
              const recencyA = a.createdAt?.toDate?.() ? a.createdAt.toDate().getTime() : 0;

              const likesB = b.likes || 0;
              const commentsB = b.commentsCount || 0;
              const recencyB = b.createdAt?.toDate?.() ? b.createdAt.toDate().getTime() : 0;

              // Fórmula de puntuación: (likes * 2) + (comentarios * 3) + factor de recencia
              const scoreA = likesA * 2 + commentsA * 3 + recencyA / 86400000;
              const scoreB = likesB * 2 + commentsB * 3 + recencyB / 86400000;

              return scoreB - scoreA; // Orden descendente por puntuación
            });

            // Limitar al número solicitado después de ordenar
            setPosts(sortedPosts.slice(0, limitCount));
            setLoading(false);
          },
          (err) => {
            console.error('Error fetching trending posts:', err);
            setError('Error al cargar las publicaciones populares');
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (err) {
        console.error('Error en fetchTrendingPosts:', err);
        setError('Error al cargar las publicaciones populares');
        setLoading(false);
        return () => { };
      }
    };

    // Aplicar el filtro seleccionado
    let unsubscribe: () => void = () => { };

    if (filter === 'following') {
      // Para posts de usuarios seguidos
      fetchFollowingPosts().then(cleanup => {
        unsubscribe = cleanup || (() => { });
      }).catch(() => {
        unsubscribe = () => { };
      });
    } else if (filter === 'trending') {
      // Para posts en tendencia
      fetchTrendingPosts().then(cleanup => {
        unsubscribe = cleanup || (() => { });
      }).catch(() => {
        unsubscribe = () => { };
      });
    } else {
      // Para los demás filtros, usamos queries directas
      const postsRef = collection(db, 'posts');
      let postsQuery;

      switch (filter) {
        case 'recent':
          postsQuery = query(
            postsRef,
            orderBy('createdAt', 'desc'),
            limit(limitCount)
          );
          break;
        case 'popular':
          postsQuery = query(
            postsRef,
            orderBy('likes', 'desc'),
            limit(limitCount)
          );
          break;
        case 'oldest':
          postsQuery = query(
            postsRef,
            orderBy('createdAt', 'asc'),
            limit(limitCount)
          );
          break;
        default:
          postsQuery = query(
            postsRef,
            orderBy('createdAt', 'desc'),
            limit(limitCount)
          );
      }

      unsubscribe = onSnapshot(
        postsQuery,
        (snapshot) => {
          const fetchedPosts: Post[] = [];
          snapshot.forEach((doc) => {
            fetchedPosts.push({ id: doc.id, ...doc.data() } as Post);
          });
          setPosts(fetchedPosts);
          setLoading(false);
        },
        (err) => {
          console.error(`Error fetching ${filter} posts:`, err);
          setError(`Error al cargar las publicaciones`);
          setLoading(false);
        }
      );
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userId, filter, limitCount]);

  return { posts, loading, error };
}

/**
 * Hook para obtener publicaciones populares que el usuario no sigue
 * @param userId ID del usuario actual
 * @param limitCount Número máximo de publicaciones a obtener
 */
export function useDiscoverPosts(userId: string | undefined, limitCount = 10) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setPosts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchDiscoverPosts = async () => {
      try {
        // Obtener IDs de usuarios seguidos
        const followingRef = collection(db, `users/${userId}/following`);
        const followingSnapshot = await getDocs(followingRef);

        const followingIds = followingSnapshot.docs.map(doc => doc.id);
        // Incluir también el ID del propio usuario
        followingIds.push(userId);

        // Firebase NOT_IN tiene un límite de 10 valores
        // Si tenemos más de 10, usamos solo los primeros 10 para el query
        // y filtramos el resto client-side
        const followingIdsForQuery = followingIds.slice(0, 10);
        const allFollowingIds = new Set(followingIds);

        // Calcular la fecha de hace 30 días
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // Consultar posts populares de usuarios NO seguidos
        const postsRef = collection(db, 'posts');
        let postsQuery;

        if (followingIdsForQuery.length > 0) {
          postsQuery = query(
            postsRef,
            where('userId', 'not-in', followingIdsForQuery),
            where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
            orderBy('createdAt', 'desc'),
            orderBy('likes', 'desc'),
            limit(limitCount * 3) // Obtenemos más para luego filtrar
          );
        } else {
          // Si no sigue a nadie, simplemente obtener posts populares
          postsQuery = query(
            postsRef,
            where('userId', '!=', userId),
            where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
            orderBy('createdAt', 'desc'),
            orderBy('likes', 'desc'),
            limit(limitCount * 2)
          );
        }

        const postsSnapshot = await getDocs(postsQuery);
        const fetchedPosts: Post[] = [];

        postsSnapshot.forEach((doc) => {
          const postData = { id: doc.id, ...doc.data() } as Post;
          // Filtrar client-side los posts de usuarios seguidos que no fueron
          // filtrados por el query (cuando hay más de 10 seguidos)
          if (!allFollowingIds.has(postData.userId)) {
            fetchedPosts.push(postData);
          }
        });

        // Ordenar por una combinación de recencia, likes y comentarios
        const sortedPosts = fetchedPosts.sort((a, b) => {
          // Calcular una puntuación basada en likes, comentarios y recencia
          const scoreA = (a.likes || 0) * 2 + (a.commentsCount || 0) * 3 +
            (a.createdAt?.toDate?.().getTime() || 0) / 86400000;
          const scoreB = (b.likes || 0) * 2 + (b.commentsCount || 0) * 3 +
            (b.createdAt?.toDate?.().getTime() || 0) / 86400000;
          return scoreB - scoreA;
        });

        // Limitar al número solicitado
        setPosts(sortedPosts.slice(0, limitCount));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching discover posts:', error);
        setError('Error al cargar las publicaciones recomendadas');
        setLoading(false);
      }
    };

    fetchDiscoverPosts();

    // No configuramos un listener en tiempo real para ahorrar recursos
    // ya que estas son recomendaciones que no necesitan actualizarse constantemente

    return () => {
      // Cleanup
    };
  }, [userId, limitCount]);

  return { posts, loading, error };
}
